import { ListChecks, Pencil, Play, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Page, PageHeader } from '../components/layout/AppShell';
import { ConfirmDialog, Modal, useToast } from '../components/ui/overlays';
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  Field,
  Input,
  LoadingBlock,
  Progress,
  Switch,
  Textarea,
} from '../components/ui/primitives';
import {
  useDeleteLeadType,
  useLeadTypes,
  useSaveLeadType,
} from '../hooks/queries';
import type { LeadType } from '../types/api';
import { LaunchAgentModal } from '../components/jobs/LaunchAgentModal';
import { formatNumber } from '../lib/utils';

const COLORS = ['#2F52EB', '#7C4DFF', '#0E9F8E', '#E08700', '#E0365E', '#0891B2', '#C026D3'];

const BLANK = {
  name: '',
  description: '',
  researchPrompt: '',
  outreachPrompt: '',
  icp: '',
  disqualifiers: '',
  sourcesHint: '',
  targetCount: 50,
  color: COLORS[0],
  isActive: true,
};

type Draft = typeof BLANK & { id?: string };

const FIELDS = [
  {
    key: 'researchPrompt' as const,
    label: 'Research prompt',
    required: true,
    rows: 12,
    hint: 'The instructions the research agent follows for this type. Say what to look for, which signals make a strong lead, and what to capture for each one.',
    placeholder:
      'Find independent creators whose audience actively rents things.\n\nLook for:\n- YouTube channels and newsletters about van life, camping, festivals\n- "best X rental" comparison articles that already use affiliate links\n\nSignals that make a strong lead:\n- They already link to a rental brand\n- Audience concentrated in the Benelux or DACH\n\nFor each lead capture: what content they make, audience size, and the best contact route.',
  },
  {
    key: 'icp' as const,
    label: 'Ideal profile',
    rows: 5,
    hint: 'Used for scoring. Be specific about size, geography and the signals that matter.',
    placeholder:
      'Creator or niche content site. Audience 5k-500k in the Benelux, DACH or Nordics. Publishes at least monthly. Already monetises somehow.',
  },
  {
    key: 'disqualifiers' as const,
    label: 'Disqualifiers',
    rows: 4,
    hint: 'Saves the agent from wasting a search. Be blunt.',
    placeholder:
      'Dormant accounts. Pure aggregators. No email address anywhere. Audiences outside Europe. Under 2k followers.',
  },
  {
    key: 'sourcesHint' as const,
    label: 'Where to look',
    rows: 4,
    hint: 'Concrete places and search operators. This is usually the highest-leverage field.',
    placeholder:
      'YouTube channel about pages, Substack and beehiiv directories, "best rental" listicles, Awin/Daisycon publisher directories, niche subreddits.',
  },
  {
    key: 'outreachPrompt' as const,
    label: 'Outreach angle',
    rows: 5,
    hint: 'How to write to this type specifically. Layered on top of the project outreach prompt.',
    placeholder:
      'Talk to them as a creator, not a media buyer. Reference the specific video or post. Lead with the commission and that setup is a link, not an integration.',
  },
];

export const LeadTypesPage = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { data: leadTypes, isLoading } = useLeadTypes(projectId);
  const saveLeadType = useSaveLeadType(projectId ?? '');
  const deleteLeadType = useDeleteLeadType(projectId ?? '');
  const toast = useToast();

  const [researchType, setResearchType] = useState<LeadType | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LeadType | null>(null);

  const openEdit = (type: LeadType) =>
    setDraft({
      id: type.id,
      name: type.name,
      description: type.description ?? '',
      researchPrompt: type.researchPrompt,
      outreachPrompt: type.outreachPrompt ?? '',
      icp: type.icp ?? '',
      disqualifiers: type.disqualifiers ?? '',
      sourcesHint: type.sourcesHint ?? '',
      targetCount: type.targetCount,
      color: type.color,
      isActive: type.isActive,
    });

  const submit = async () => {
    if (!draft) return;
    if (!draft.name.trim()) {
      toast.error('Give the lead type a name');
      return;
    }
    if (draft.researchPrompt.trim().length < 10) {
      toast.error('The research prompt is what the agent reads — write at least a few lines');
      return;
    }
    try {
      await saveLeadType.mutateAsync({
        id: draft.id,
        name: draft.name.trim(),
        description: draft.description || undefined,
        researchPrompt: draft.researchPrompt,
        outreachPrompt: draft.outreachPrompt || undefined,
        icp: draft.icp || undefined,
        disqualifiers: draft.disqualifiers || undefined,
        sourcesHint: draft.sourcesHint || undefined,
        targetCount: draft.targetCount,
        color: draft.color,
        isActive: draft.isActive,
      });
      toast.success(draft.id ? 'Lead type updated' : 'Lead type created');
      setDraft(null);
    } catch (error) {
      toast.error(error);
    }
  };

  if (isLoading) return <LoadingBlock />;

  return (
    <Page>
      <PageHeader
        title="Lead types"
        description="Each type is a category of lead with its own prompt, ideal profile and target. This is where the research agent gets its brief."
        actions={
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setDraft({ ...BLANK })}>
            New lead type
          </Button>
        }
      />

      {leadTypes?.length ? (
        <div className="grid gap-3 lg:grid-cols-2">
          {leadTypes.map((type) => (
            <Card key={type.id} className="flex flex-col">
              <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: type.color }} />
                    <h2 className="truncate text-[15px] font-semibold text-ink">{type.name}</h2>
                    {!type.isActive ? <Badge tone="muted">paused</Badge> : null}
                  </div>
                  {type.description ? (
                    <p className="mt-1 line-clamp-2 text-[13px] text-muted">{type.description}</p>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Edit"
                    onClick={() => openEdit(type)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Delete"
                    onClick={() => setDeleteTarget(type)}
                  >
                    <Trash2 className="h-4 w-4 text-danger" />
                  </Button>
                </div>
              </div>

              <CardBody className="flex-1 space-y-3">
                <div>
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="text-muted">
                      {formatNumber(type.stats?.leads ?? 0)} of {formatNumber(type.targetCount)} leads
                    </span>
                    <span className="tabular-nums text-faint">
                      {formatNumber(type.stats?.contacted ?? 0)} contacted
                    </span>
                  </div>
                  <Progress
                    value={type.stats?.leads ?? 0}
                    max={type.targetCount || 1}
                    color={type.color}
                  />
                </div>

                <pre className="max-h-24 overflow-hidden whitespace-pre-wrap rounded-lg bg-raised px-3 py-2 font-mono text-[11px] leading-relaxed text-muted">
                  {type.researchPrompt.slice(0, 320)}
                  {type.researchPrompt.length > 320 ? '…' : ''}
                </pre>
              </CardBody>

              <div className="flex items-center justify-between gap-2 border-t border-line px-5 py-3">
                <span className="text-2xs text-faint">
                  {type.icp ? 'ICP set' : 'No ICP'} ·{' '}
                  {type.sourcesHint ? 'sources set' : 'no source hints'}
                </span>
                <Button
                  variant="subtle"
                  size="sm"
                  icon={<Play className="h-3.5 w-3.5" />}
                  onClick={() => setResearchType(type)}
                  disabled={!type.isActive}
                >
                  Queue research
                </Button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <EmptyState
            icon={<ListChecks className="h-5 w-5" />}
            title="No lead types yet"
            description="Create one per category you want to reach — affiliates, B2B partners, marketplaces. Each gets its own prompt so the agent knows exactly what to look for."
            action={
              <Button variant="primary" onClick={() => setDraft({ ...BLANK })}>
                Create the first lead type
              </Button>
            }
          />
        </Card>
      )}

      <LaunchAgentModal open={Boolean(researchType)} onClose={() => setResearchType(null)} projectId={projectId ?? ''} initialType="RESEARCH" leadTypeId={researchType?.id} targetNewLeads={researchType ? Math.max(10, researchType.targetCount - (researchType.stats?.leads ?? 0)) : undefined} />

      <Modal
        open={Boolean(draft)}
        onClose={() => setDraft(null)}
        title={draft?.id ? 'Edit lead type' : 'New lead type'}
        description="The prompt fields are what make or break lead quality. Write them like a brief for a new colleague."
        width="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submit} loading={saveLeadType.isPending}>
              {draft?.id ? 'Save changes' : 'Create lead type'}
            </Button>
          </>
        }
      >
        {draft ? (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name" htmlFor="lt-name">
                <Input
                  id="lt-name"
                  autoFocus
                  value={draft.name}
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                  placeholder="Affiliates & creators"
                />
              </Field>
              <Field
                label="Target count"
                htmlFor="lt-target"
                hint="Progress is measured against this."
              >
                <Input
                  id="lt-target"
                  type="number"
                  min={1}
                  value={draft.targetCount}
                  onChange={(event) =>
                    setDraft({ ...draft, targetCount: Number(event.target.value) || 1 })
                  }
                />
              </Field>
            </div>

            <Field label="Description" htmlFor="lt-description">
              <Input
                id="lt-description"
                value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                placeholder="Content creators whose audience rents equipment or vehicles"
              />
            </Field>

            {FIELDS.map((field) => (
              <Field
                key={field.key}
                label={field.required ? `${field.label} *` : field.label}
                hint={field.hint}
                htmlFor={`lt-${field.key}`}
              >
                <Textarea
                  id={`lt-${field.key}`}
                  rows={field.rows}
                  className="prompt-input"
                  value={draft[field.key]}
                  placeholder={field.placeholder}
                  onChange={(event) => setDraft({ ...draft, [field.key]: event.target.value })}
                />
              </Field>
            ))}

            <div className="flex flex-wrap items-center justify-between gap-4 border-t border-line pt-4">
              <div>
                <span className="field-label">Colour</span>
                <div className="flex gap-1.5">
                  {COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      aria-label={`Use ${color}`}
                      onClick={() => setDraft({ ...draft, color })}
                      className={`h-6 w-6 rounded-full transition-transform ${
                        draft.color === color ? 'scale-110 ring-2 ring-brand ring-offset-2 ring-offset-surface' : ''
                      }`}
                      style={{ background: color }}
                    />
                  ))}
                </div>
              </div>
              <Switch
                checked={draft.isActive}
                onChange={(value) => setDraft({ ...draft, isActive: value })}
                label="Active"
                description="Inactive types are skipped by research agents."
              />
            </div>
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Delete lead type"
        destructive
        confirmLabel="Delete"
        loading={deleteLeadType.isPending}
        message={
          <>
            Delete <strong className="text-ink">{deleteTarget?.name}</strong>? Its leads are kept but
            become unassigned.
          </>
        }
        onConfirm={async () => {
          if (!deleteTarget) return;
          try {
            await deleteLeadType.mutateAsync(deleteTarget.id);
            toast.success('Lead type deleted');
            setDeleteTarget(null);
          } catch (error) {
            toast.error(error);
          }
        }}
      />
    </Page>
  );
};
